import { API_BASE_URL } from "../../../../config/api";
import { getCompany } from "../../../../shared/data/storage/authStorage";

/**
 * API Error class
 */
class APIError extends Error {
  constructor(message: string, public status: number, public data?: any) {
    super(message);
    this.name = "APIError";
  }
}

const getStoredCompanyId = (): number => {
  const rawCompanyId = getCompany()?.companyId;
  const companyId = rawCompanyId ? Number.parseInt(String(rawCompanyId), 10) : NaN;

  if (!Number.isFinite(companyId) || companyId <= 0) {
    throw new APIError("Company details not found. Please sign in again.", 400);
  }

  return companyId;
};

/**
 * Databook Rule interface
 */
export interface DatabookRule {
  id: number;
  company_id: number;
  file_name: string;
  uploaded_by: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Upload Rule Response interface
 */
export interface UploadRuleResponse {
  success: boolean;
  message: string;
  data?: {
    rule_id: number;
    file_name: string;
  };
}

/**
 * Get Rules Response interface
 */
export interface GetRulesResponse {
  success: boolean;
  data: DatabookRule[];
}

/**
 * Upload a databook rule Excel file
 * @param file Excel file to upload
 * @param companyId Company ID
 * @returns Upload response
 */
export const uploadDatabookRule = async (
  file: File,
  companyId: number = getStoredCompanyId()
): Promise<UploadRuleResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("company_id", String(companyId));

  const response = await fetch(`${API_BASE_URL}/api/databook-rules/upload`, {
    method: "POST",
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Upload failed: ${response.statusText}`,
      response.status,
      data
    );
  }

  return data as UploadRuleResponse;
};

/**
 * Get all databook rules for a company
 * @param companyId Company ID
 * @returns List of rules
 */
export const getDatabookRules = async (
  companyId: number = getStoredCompanyId()
): Promise<GetRulesResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/databook-rules/list?company_id=${companyId}`,
    {
      method: "GET",
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to get rules: ${response.statusText}`,
      response.status,
      data
    );
  }

  return data as GetRulesResponse;
};

/**
 * Download a databook rule file
 * @param ruleId Rule ID
 * @param fileName File name for download
 */
export const downloadDatabookRule = (ruleId: number, fileName: string): void => {
  const url = `${API_BASE_URL}/api/databook-rules/download/${ruleId}/${encodeURIComponent(
    fileName
  )}`;
  window.open(url, "_blank");
};

